import React, { useEffect, useState } from "react";
import {
    Alert, Box, Button, ExpandableSection, FileUpload, Link, Modal, SpaceBetween, StatusIndicator, Table, Tabs, TextContent,
} from "@amzn/awsui-components-react";
import { CodeView } from '@amzn/awsui-code-view';
import { useSelector } from "react-redux";
import { createFlexicoTemplate } from "src/utilities/FlexicoAPIs";
import { checkGlueJobExist } from "src/utilities/AppSyncPlusAPIs";
import { createAuditLog } from "src/Ateam-BI/Templates/Utils/AppActivityUtils";
import { getEditableFields } from "src/Ateam-BI/Templates/Utils/FormMetadata";
import { downloadJsonData } from "src/Ateam-BI/utils/generalUtils";
import { CONTICO_JOB_LABEL, FALLBACK_TRIGGER_TYPES, SAMPLE_FILE, USER_GUIDE_URL } from "./flexicoTemplateSpec";
import { buildCreatePayload, checkConticoJobs, parseTemplatesFile, validateTemplates } from "./flexicoTemplateValidation";

const SAMPLE_FILE_NAME = 'flexico_templates_sample.json';

const checkGlueJob = (jobName, critical) => new Promise((resolve, reject) => {
    checkGlueJobExist(jobName, critical,
        (result) => resolve(result?.data?.checkGlueJobExist?.status === 'true'),
        (error) => reject(new Error(error?.errors?.[0]?.message)))
});

// Same action as the form ("ADD REQ"), so existing audit views and filters keep working;
// the comment says it came from Programmatic add, which file, and which template in it.
export const PROGRAMMATIC_ADD_AUDIT_PREFIX = 'Adding a template via Programmatic add (JSON upload)';
const programmaticAuditComment = (fileName, position, total) => {
    // The comment is placed inside a GraphQL string literal, so keep only safe characters.
    const safeName = String(fileName || 'unnamed file').replace(/[^A-Za-z0-9._ -]/g, '_').slice(0, 120);
    return `${PROGRAMMATIC_ADD_AUDIT_PREFIX} - file ${safeName}, template ${position} of ${total}`;
};

const auditCreate = (payload, userId, comment) => new Promise((resolve, reject) => {
    createAuditLog('FLEXICO', `ADD_REQ#${Date.now()}`, comment, JSON.stringify({}), JSON.stringify(payload), "ADD REQ", userId,
        resolve, (error) => reject(new Error(`Could not log audit, so the template was not created: ${error?.errors?.[0]?.message || ''}`)))
});

// The add-request entry above is keyed "FLEXICO" (the template has no id yet), so it does not
// show on the template's own audit page. Record the origin there too, once the id exists.
export const PROGRAMMATIC_ADD_AUDIT_ACTION = 'CREATED VIA PROGRAMMATIC ADD';
const auditCreatedTemplate = (flexId, payload, userId, comment) => new Promise((resolve, reject) => {
    createAuditLog(`FLEXICO#${flexId}`, `${Date.now()}`, comment.replace(PROGRAMMATIC_ADD_AUDIT_PREFIX, 'Created via Programmatic add (JSON upload)'),
        JSON.stringify({}), JSON.stringify(payload), PROGRAMMATIC_ADD_AUDIT_ACTION, userId, resolve, reject)
});

const createTemplate = (payload) => new Promise((resolve, reject) => {
    // createFlexicoTemplate cleans its input in place, so give it a copy.
    createFlexicoTemplate(JSON.parse(JSON.stringify(payload)),
        (response) => resolve(response?.data?.createFlexicoTemplate),
        (error) => reject(new Error(error?.errors?.[0]?.message || 'Create failed')))
});

const STATUS = {
    invalid: (r) => <StatusIndicator type="error">{r.errors.length} issue{r.errors.length > 1 ? 's' : ''}</StatusIndicator>,
    ready: () => <StatusIndicator type="success">Ready</StatusIndicator>,
    waiting: () => <StatusIndicator type="pending">Waiting</StatusIndicator>,
    creating: () => <StatusIndicator type="in-progress">Creating</StatusIndicator>,
    created: () => <StatusIndicator type="success">Created</StatusIndicator>,
    failed: () => <StatusIndicator type="error">Failed</StatusIndicator>,
};

const Instructions = ({ onDownloadSample }) => (
    <SpaceBetween size="m">
        <Box variant="p">
            Create one or more FlexiCo templates from a JSON file instead of filling in the form. Each template goes through the same
            checks as the form and is saved with the same API, so the result is identical to adding it by hand.
        </Box>
        <Box>
            <Link external href={USER_GUIDE_URL}>User guide and field reference</Link>
            <Box variant="small" color="text-body-secondary">Every option you can put in the file, with what it does in the form.</Box>
        </Box>
        <TextContent><ol>
            <li>Download the sample file and replace the example values with yours. Use the <b>field reference</b> in the user guide for every option.</li>
            <li>Upload the file on the <b>Upload</b> tab. Every template is checked, including that the <code>{CONTICO_JOB_LABEL}</code> Glue job exists.</li>
            <li>Fix anything listed and upload again. Nothing is created until every template in the file passes.</li>
            <li>Choose <b>Create templates</b>. Each one is created in turn and you see the result for each.</li>
        </ol></TextContent>
        <Alert type="info" header="Set up afterwards in Settings">
            Ripples, TANC and "Show parameters in history" are not part of the file. Open the new template's Settings to add them.
            Fields such as app, Step Function and status are filled in for you; if your file has them (for example, a template copied
            from Download App), they are ignored.
        </Alert>
        <Alert type="info" header="Tip: let an AI agent write the file">
            Get the agent guide from the <Link external href={USER_GUIDE_URL}>user guide folder</Link> and give it to your agent
            (for example Claude or Cline) together with what you need, such as "a Parameters only template that runs my_glue_job
            with a region dropdown". The guide has every option, rule and an example, so the agent can produce a file that passes
            on the first upload.
        </Alert>
        <Button iconName="download" onClick={onDownloadSample}>Download sample JSON</Button>
        <ExpandableSection headerText="Sample file">
            <CodeView content={JSON.stringify(SAMPLE_FILE, null, 2)} lineNumbers />
        </ExpandableSection>
    </SpaceBetween>
);

const ProgrammaticAddFlexicoModal = ({ appId, canAdd, onDismiss, onCreated }) => {
    const currentStage = useSelector((state) => state.auth.currentStage);
    const userId = useSelector((state) => state.auth.userId);
    const [activeTab, setActiveTab] = useState('instructions');
    const [triggerTypes, setTriggerTypes] = useState(null);
    const [triggerTypesFallback, setTriggerTypesFallback] = useState(false);
    const [files, setFiles] = useState([]);
    const [fileError, setFileError] = useState(null);
    const [rows, setRows] = useState([]);
    const [phase, setPhase] = useState('idle'); // idle | checking | checked | creating | done

    useEffect(() => {
        let cancelled = false;
        getEditableFields().then((res) => {
            const values = (res?.flexico_trigger_type?.values || []).map((v) => v?.value).filter(Boolean);
            if (cancelled) return;
            setTriggerTypes(values.length ? values : FALLBACK_TRIGGER_TYPES);
            setTriggerTypesFallback(!values.length);
        }).catch(() => {
            if (cancelled) return;
            setTriggerTypes(FALLBACK_TRIGGER_TYPES);
            setTriggerTypesFallback(true);
        });
        return () => { cancelled = true; };
    }, []);

    const checkFile = async (file) => {
        setFileError(null);
        setRows([]);
        if (!file) { setPhase('idle'); return; }
        setPhase('checking');
        // Glue lookup failures are reported per template by checkConticoJobs; this catches the rest
        // (reading the file, anything unexpected) so the spinner never gets stuck.
        try {
            const { templates, error } = parseTemplatesFile(await file.text());
            if (error) { setFileError(error); setPhase('idle'); return; }
            const results = await checkConticoJobs(validateTemplates(templates, { triggerTypes }), checkGlueJob);
            setRows(results.map((r, index) => ({
                index,
                name: r.template?.template_name || `Template ${index + 1}`,
                triggerType: r.template?.trigger_type,
                parameterCount: Array.isArray(r.template?.parameters) ? r.template.parameters.length : 0,
                template: r.template,
                errors: r.errors,
                warnings: r.warnings,
                status: r.errors.length ? 'invalid' : 'ready',
                message: null,
            })));
            setPhase('checked');
        } catch (e) {
            setFileError(e?.message || 'Could not check the templates. Please try again.');
            setPhase('idle');
        }
    };

    const onFileChange = ({ detail }) => {
        setFiles(detail.value);
        checkFile(detail.value[0]);
    };

    const allValid = rows.length > 0 && rows.every((r) => r.errors.length === 0);
    const updateRow = (index, patch) => setRows((prev) => prev.map((r) => (r.index === index ? { ...r, ...patch } : r)));

    const createAll = async () => {
        setPhase('creating');
        setRows((prev) => prev.map((r) => ({ ...r, status: 'waiting' })));
        const createdIds = [];
        for (const row of rows) {
            updateRow(row.index, { status: 'creating' });
            try {
                const payload = buildCreatePayload(row.template, { appId, userId, currentStage });
                const comment = programmaticAuditComment(files[0]?.name, row.index + 1, rows.length);
                await auditCreate(payload, userId, comment);
                const created = await createTemplate(payload);
                createdIds.push(created?.flex_id);
                let message = created?.flex_id ? `flex_id ${created.flex_id}` : null;
                if (created?.flex_id) {
                    // The template exists either way; a failed note only means the origin is not on its audit page.
                    await auditCreatedTemplate(created.flex_id, payload, userId, comment)
                        .catch(() => { message = `${message}. Created, but the Programmatic add note could not be added to its audit log.`; });
                }
                updateRow(row.index, { status: 'created', message });
            } catch (e) {
                updateRow(row.index, { status: 'failed', message: e.message });
            }
        }
        setPhase('done');
        if (createdIds.filter(Boolean).length) onCreated?.(createdIds.filter(Boolean)[0]);
    };

    const createdCount = rows.filter((r) => r.status === 'created').length;
    const failedCount = rows.filter((r) => r.status === 'failed').length;
    const busy = phase === 'checking' || phase === 'creating';

    const uploadTab = (
        <SpaceBetween size="m">
            {!canAdd && <Alert type="error" header="Access denied">You do not have access to add processes to this app.</Alert>}
            {triggerTypesFallback && <Alert type="warning">The list of input types could not be loaded; using the standard four.</Alert>}
            <FileUpload
                onChange={onFileChange}
                value={files}
                accept="application/json,.json"
                showFileLastModified
                showFileSize
                constraintText="A .json file with one template, a list of templates, or { &quot;templates&quot;: [...] }."
                i18nStrings={{ uploadButtonText: () => 'Choose file', dropzoneText: () => 'Drop file to upload', removeFileAriaLabel: () => 'Remove file', errorIconAriaLabel: 'Error' }}
            />
            {fileError && <Alert type="error" header="Could not read the file">{fileError}</Alert>}
            {phase === 'checking' && <StatusIndicator type="loading">Checking templates…</StatusIndicator>}
            {rows.length > 0 && (
                <Table
                    variant="container"
                    wrapLines
                    items={rows}
                    header={<Box variant="h3">{rows.length} template{rows.length > 1 ? 's' : ''} in file</Box>}
                    columnDefinitions={[
                        { id: 'name', header: 'Template', cell: (r) => r.name, width: 220 },
                        { id: 'trigger', header: 'Input type', cell: (r) => r.triggerType || '—', width: 190 },
                        { id: 'params', header: 'Parameters', cell: (r) => r.parameterCount, width: 110 },
                        { id: 'status', header: 'Status', cell: (r) => STATUS[r.status](r), width: 120 },
                        { id: 'details', header: 'Details', cell: (r) => (
                            <SpaceBetween size="xxs">
                                {r.message && <Box color={r.status === 'failed' ? 'text-status-error' : 'text-body-secondary'}>{r.message}</Box>}
                                {r.errors.map((e, i) => <Box key={`e${i}`} color="text-status-error">{e}</Box>)}
                                {r.warnings.map((w, i) => <Box key={`w${i}`} color="text-status-warning">{w}</Box>)}
                            </SpaceBetween>
                        ) },
                    ]}
                />
            )}
            {phase === 'checked' && rows.length > 0 && !allValid && (
                <Alert type="error">Fix the issues above and upload the file again. Nothing has been created.</Alert>
            )}
            {phase === 'done' && (
                <Alert type={failedCount ? 'warning' : 'success'} header={`${createdCount} of ${rows.length} created`}>
                    {failedCount
                        ? 'Templates that failed were not created. Remove the created ones from the file, fix the rest and upload it again.'
                        : 'The new templates are in the list on the left. Add ripples or TANC from each template\'s Settings if you need them.'}
                </Alert>
            )}
        </SpaceBetween>
    );

    return (
        <Modal
            visible
            size="max"
            onDismiss={() => !busy && onDismiss()}
            header="Programmatic add: FlexiCo templates"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" disabled={busy} onClick={onDismiss}>{phase === 'done' ? 'Close' : 'Cancel'}</Button>
                        {phase !== 'done' && (
                            <Button variant="primary" loading={phase === 'creating'}
                                disabled={!canAdd || !allValid || phase !== 'checked' || !triggerTypes}
                                onClick={createAll}>
                                Create {rows.length > 1 ? `${rows.length} templates` : 'template'}
                            </Button>
                        )}
                    </SpaceBetween>
                </Box>
            }
        >
            <Tabs activeTabId={activeTab} onChange={({ detail }) => setActiveTab(detail.activeTabId)} tabs={[
                { id: 'instructions', label: 'Instructions', content: <Instructions onDownloadSample={() => downloadJsonData(SAMPLE_FILE_NAME, SAMPLE_FILE)} /> },
                { id: 'upload', label: 'Upload', disabled: !triggerTypes, content: uploadTab },
            ]} />
        </Modal>
    );
};

export default ProgrammaticAddFlexicoModal;
